import { ClipboardList } from 'lucide-react';
import { Section, Field, EditButton, EditActions } from '../ui';
import { ic } from '../helpers';

// ─────────────────────────────────────────
// LIABILITY & SCHEDULED PROPERTY SECTION
// Read mode shows a compact valuables table only for categories that
// have any value. Edit mode reveals 6 ownership toggles, each with
// scheduled/blanket dollar inputs. The bank-vault sub-input only
// appears for jewelry.
// ─────────────────────────────────────────
export default function LiabilitySection({
  client,
  editing,
  setEditing,
  liabilityDraft,
  setLiabilityDraft,
  saving,
  onSave,
}) {
  const hasAmt = (k) => client[k] != null && Number(client[k]) > 0;

  // Open the edit panel and pre-populate the ownsX checkboxes based on
  // whether each category already has a non-zero amount.
  const onStartEdit = () => {
    setLiabilityDraft({
      ...client,
      ownsJewelry:  hasAmt('jewelryScheduledAmount') || hasAmt('jewelryBlanketAmount') || hasAmt('jewelryInBankVault'),
      ownsFineArt:  hasAmt('fineArtScheduledAmount') || hasAmt('fineArtBlanketAmount'),
      ownsCoins:    hasAmt('coinsScheduledAmount')   || hasAmt('coinsBlanketAmount'),
      ownsWine:     hasAmt('wineScheduledAmount')    || hasAmt('wineBlanketAmount'),
      ownsMusical:  hasAmt('musicalScheduledAmount') || hasAmt('musicalBlanketAmount'),
      ownsMisc:     hasAmt('miscScheduledAmount'),
    });
    setEditing(true);
  };

  const onCancel = () => {
    setLiabilityDraft(client);
    setEditing(false);
  };

  return (
    <Section
      title="Liability & Scheduled Property"
      icon={ClipboardList}
      action={editing
        ? <EditActions onSave={onSave} onCancel={onCancel} saving={saving} />
        : <EditButton onClick={onStartEdit} />}
    >
      {!editing ? (
        <div className="pt-3 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Umbrella Policy" value={client.hasUmbrella ? 'Yes' : 'No'} editing={false} />
            <Field
              label="Rental/Secondary Homes"
              value={client.hasRentalHomes ? `Yes (${client.rentalHomesCount || '?'})` : 'No'}
              editing={false}
            />
          </div>

          {/* Valuables — compact table, only categories that have values */}
          {(() => {
            const fmt = (v) => (v != null && Number(v) > 0) ? `$${Number(v).toLocaleString()}` : null;
            const rows = [
              ['Jewelry', client.jewelryScheduledAmount, client.jewelryBlanketAmount, client.jewelryInBankVault],
              ['Fine Art / Collectibles', client.fineArtScheduledAmount, client.fineArtBlanketAmount, null],
              ['Coins / Silver / Stamps / Furs', client.coinsScheduledAmount, client.coinsBlanketAmount, null],
              ['Wine', client.wineScheduledAmount, client.wineBlanketAmount, null],
              ['Musical Instruments', client.musicalScheduledAmount, client.musicalBlanketAmount, null],
              ['Miscellaneous', client.miscScheduledAmount, null, null],
            ].filter(([, s, b, v]) => fmt(s) || fmt(b) || fmt(v));

            if (rows.length === 0) {
              return <p className="text-xs text-slate-500">No valuables coverage on file.</p>;
            }

            return (
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Valuables Coverage</p>
                <div className="border border-slate-200 rounded-xl overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-xs text-slate-500">
                      <tr>
                        <th className="text-left px-3 py-2 font-medium">Category</th>
                        <th className="text-right px-3 py-2 font-medium">Scheduled</th>
                        <th className="text-right px-3 py-2 font-medium">Blanket</th>
                        <th className="text-right px-3 py-2 font-medium">Bank Vault</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {rows.map(([name, s, b, v]) => (
                        <tr key={name}>
                          <td className="px-3 py-2 text-slate-700">{name}</td>
                          <td className="px-3 py-2 text-right text-slate-700">{fmt(s) || '—'}</td>
                          <td className="px-3 py-2 text-right text-slate-700">{fmt(b) || '—'}</td>
                          <td className="px-3 py-2 text-right text-slate-700">{fmt(v) || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })()}
        </div>
      ) : (
        <div className="pt-3 space-y-4">
          {/* Top row: umbrella + rental homes */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Umbrella Policy</label>
              <select
                className={ic}
                value={liabilityDraft.hasUmbrella ? 'Yes' : 'No'}
                onChange={e => setLiabilityDraft(d => ({ ...d, hasUmbrella: e.target.value === 'Yes' }))}
              >
                <option>No</option><option>Yes</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Rental/Secondary Homes</label>
              <select
                className={ic}
                value={liabilityDraft.hasRentalHomes ? 'Yes' : 'No'}
                onChange={e => setLiabilityDraft(d => ({ ...d, hasRentalHomes: e.target.value === 'Yes' }))}
              >
                <option>No</option><option>Yes</option>
              </select>
            </div>
            {liabilityDraft.hasRentalHomes && (
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">How many?</label>
                <input
                  type="number"
                  min="1"
                  className={ic}
                  value={liabilityDraft.rentalHomesCount || ''}
                  onChange={e => setLiabilityDraft(d => ({ ...d, rentalHomesCount: e.target.value }))}
                />
              </div>
            )}
          </div>

          {/* Valuables — 6 toggles + amount inputs */}
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Valuables Coverage</p>
            <div className="bg-slate-50 rounded-xl px-4 divide-y divide-slate-200">

              {/* Jewelry */}
              <label className="flex items-center justify-between py-3 cursor-pointer">
                <span className="text-sm text-slate-700">Owns jewelry?</span>
                <input
                  type="checkbox"
                  checked={!!liabilityDraft.ownsJewelry}
                  onChange={e => setLiabilityDraft(d => ({ ...d, ownsJewelry: e.target.checked }))}
                  className="h-4 w-4"
                />
              </label>
              {liabilityDraft.ownsJewelry && (
                <div className="py-3 grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Scheduled ($)</label>
                    <input type="number" min="0" className={ic} value={liabilityDraft.jewelryScheduledAmount || ''} onChange={e => setLiabilityDraft(d => ({ ...d, jewelryScheduledAmount: e.target.value }))} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Blanket ($)</label>
                    <input type="number" min="0" className={ic} value={liabilityDraft.jewelryBlanketAmount || ''} onChange={e => setLiabilityDraft(d => ({ ...d, jewelryBlanketAmount: e.target.value }))} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Bank Vault ($)</label>
                    <input type="number" min="0" className={ic} value={liabilityDraft.jewelryInBankVault || ''} onChange={e => setLiabilityDraft(d => ({ ...d, jewelryInBankVault: e.target.value }))} />
                  </div>
                </div>
              )}

              {/* Fine Art */}
              <label className="flex items-center justify-between py-3 cursor-pointer">
                <span className="text-sm text-slate-700">Owns fine art / collectibles?</span>
                <input
                  type="checkbox"
                  checked={!!liabilityDraft.ownsFineArt}
                  onChange={e => setLiabilityDraft(d => ({ ...d, ownsFineArt: e.target.checked }))}
                  className="h-4 w-4"
                />
              </label>
              {liabilityDraft.ownsFineArt && (
                <div className="py-3 grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Scheduled ($)</label>
                    <input type="number" min="0" className={ic} value={liabilityDraft.fineArtScheduledAmount || ''} onChange={e => setLiabilityDraft(d => ({ ...d, fineArtScheduledAmount: e.target.value }))} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Blanket ($)</label>
                    <input type="number" min="0" className={ic} value={liabilityDraft.fineArtBlanketAmount || ''} onChange={e => setLiabilityDraft(d => ({ ...d, fineArtBlanketAmount: e.target.value }))} />
                  </div>
                </div>
              )}

              {/* Coins */}
              <label className="flex items-center justify-between py-3 cursor-pointer">
                <span className="text-sm text-slate-700">Owns coins / silver / stamps / furs?</span>
                <input
                  type="checkbox"
                  checked={!!liabilityDraft.ownsCoins}
                  onChange={e => setLiabilityDraft(d => ({ ...d, ownsCoins: e.target.checked }))}
                  className="h-4 w-4"
                />
              </label>
              {liabilityDraft.ownsCoins && (
                <div className="py-3 grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Scheduled ($)</label>
                    <input type="number" min="0" className={ic} value={liabilityDraft.coinsScheduledAmount || ''} onChange={e => setLiabilityDraft(d => ({ ...d, coinsScheduledAmount: e.target.value }))} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Blanket ($)</label>
                    <input type="number" min="0" className={ic} value={liabilityDraft.coinsBlanketAmount || ''} onChange={e => setLiabilityDraft(d => ({ ...d, coinsBlanketAmount: e.target.value }))} />
                  </div>
                </div>
              )}

              {/* Wine */}
              <label className="flex items-center justify-between py-3 cursor-pointer">
                <span className="text-sm text-slate-700">Owns a wine collection?</span>
                <input
                  type="checkbox"
                  checked={!!liabilityDraft.ownsWine}
                  onChange={e => setLiabilityDraft(d => ({ ...d, ownsWine: e.target.checked }))}
                  className="h-4 w-4"
                />
              </label>
              {liabilityDraft.ownsWine && (
                <div className="py-3 grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Scheduled ($)</label>
                    <input type="number" min="0" className={ic} value={liabilityDraft.wineScheduledAmount || ''} onChange={e => setLiabilityDraft(d => ({ ...d, wineScheduledAmount: e.target.value }))} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Blanket ($)</label>
                    <input type="number" min="0" className={ic} value={liabilityDraft.wineBlanketAmount || ''} onChange={e => setLiabilityDraft(d => ({ ...d, wineBlanketAmount: e.target.value }))} />
                  </div>
                </div>
              )}

              {/* Musical Instruments */}
              <label className="flex items-center justify-between py-3 cursor-pointer">
                <span className="text-sm text-slate-700">Owns musical instruments?</span>
                <input
                  type="checkbox"
                  checked={!!liabilityDraft.ownsMusical}
                  onChange={e => setLiabilityDraft(d => ({ ...d, ownsMusical: e.target.checked }))}
                  className="h-4 w-4"
                />
              </label>
              {liabilityDraft.ownsMusical && (
                <div className="py-3 grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Scheduled ($)</label>
                    <input type="number" min="0" className={ic} value={liabilityDraft.musicalScheduledAmount || ''} onChange={e => setLiabilityDraft(d => ({ ...d, musicalScheduledAmount: e.target.value }))} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Blanket ($)</label>
                    <input type="number" min="0" className={ic} value={liabilityDraft.musicalBlanketAmount || ''} onChange={e => setLiabilityDraft(d => ({ ...d, musicalBlanketAmount: e.target.value }))} />
                  </div>
                </div>
              )}

              {/* Miscellaneous */}
              <label className="flex items-center justify-between py-3 cursor-pointer">
                <span className="text-sm text-slate-700">Other miscellaneous valuables?</span>
                <input
                  type="checkbox"
                  checked={!!liabilityDraft.ownsMisc}
                  onChange={e => setLiabilityDraft(d => ({ ...d, ownsMisc: e.target.checked }))}
                  className="h-4 w-4"
                />
              </label>
              {liabilityDraft.ownsMisc && (
                <div className="py-3">
                  <label className="block text-xs font-medium text-slate-500 mb-1">Scheduled ($)</label>
                  <input
                    type="number"
                    min="0"
                    className={ic}
                    value={liabilityDraft.miscScheduledAmount || ''}
                    onChange={e => setLiabilityDraft(d => ({ ...d, miscScheduledAmount: e.target.value }))}
                  />
                  <p className="text-xs text-slate-500 mt-1">Miscellaneous items must be individually scheduled — no blanket option.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </Section>
  );
}